/** Zugriff auf die PolitWatch-API. */

import { useCallback, useEffect, useRef, useState } from "react";

const BASE = import.meta.env.VITE_API_BASE ?? "";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

type QueryValue = string | number | boolean | null | undefined;

export function buildUrl(path: string, params: Record<string, QueryValue> = {}) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined && value !== "") {
      search.set(key, String(value));
    }
  }
  const qs = search.toString();
  return `${BASE}${path}${qs ? `?${qs}` : ""}`;
}

export async function fetchJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(url, { signal, headers: { Accept: "application/json" } });
  if (!response.ok) {
    let detail = `HTTP ${response.status}`;
    try {
      const body = await response.json();
      if (body?.detail) detail = String(body.detail);
    } catch {
      /* Antwort ohne JSON-Körper */
    }
    throw new ApiError(detail, response.status);
  }
  return response.json() as Promise<T>;
}

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * Laedt eine URL und bricht bei Wechsel oder Abbau der Komponente ab.
 * Ohne den Abbruch koennte eine spaet eintreffende Antwort eine neuere
 * ueberschreiben -- bei schnell wechselnden Filtern gut zu beobachten.
 */
export function useApi<T>(url: string | null): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(url !== null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const current = useRef(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (url === null) {
      setData(null);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    const run = ++current.current;

    setLoading(true);
    setError(null);

    fetchJson<T>(url, controller.signal)
      .then((result) => {
        if (run === current.current) {
          setData(result);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted || run !== current.current) return;
        setError(err instanceof Error ? err.message : "Unbekannter Fehler");
        setLoading(false);
      });

    return () => controller.abort();
  }, [url, nonce]);

  return { data, loading, error, reload };
}

/** Verzoegert einen schnell wechselnden Wert, damit nicht jede Taste eine Abfrage ausloest. */
export function useDebounced<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

// --- Datentypen -----------------------------------------------------------

export interface Stats {
  counts: {
    parteien: number;
    parlamente: number;
    wahlperioden: number;
    vorgaenge: number;
    abstimmungen: number;
    einzelstimmen: number;
    programme: number;
    versprechen: number;
    belege: number;
    bewertungen: number;
    abstimmungen_verknuepft: number;
  };
  letzter_lauf: {
    source: string;
    scope: string | null;
    status: string;
    started_at: string;
    finished_at: string | null;
    n_written: number | null;
  } | null;
}

export interface Party {
  id: number;
  name: string;
  short_name: string;
  color: string | null;
  rolle: "regierung" | "opposition" | null;
  versprechen: number;
}

/** Urheber einer Vorlage. `short_name` ist null, wenn der Urheber keine
 *  Partei ist — etwa die Bundesregierung oder ein Ministerium. */
export interface Initiator {
  name: string;
  short_name: string | null;
  color: string | null;
}

export interface Poll {
  id: number;
  aw_poll_id: number;
  label: string;
  poll_date: string | null;
  accepted: boolean | null;
  committee: string[] | null;
  topics: string[] | null;
  vorgang_id: number | null;
  vorgang_titel: string | null;
  vorgang_typ: string | null;
  beratungsstand: string | null;
  urheber: Initiator[];
}

export interface FractionVote {
  fraction_label: string | null;
  short_name: string | null;
  color: string | null;
  n_yes: number;
  n_no: number;
  n_abstain: number;
  n_no_show: number;
  n_total: number;
  majority_vote: "yes" | "no" | "gespalten";
  hat_eingebracht: boolean;
}

export interface PollDetailData {
  poll: Poll & {
    intro_html: string | null;
    vorgang_abstract: string | null;
    dip_id: string | null;
    drucksache_nummern: string[] | null;
    sachgebiet: string[] | null;
  };
  fraktionen: FractionVote[];
}

export interface Vorgang {
  id: number;
  dip_id: string;
  typ: string | null;
  titel: string;
  beratungsstand: string | null;
  initiative: string[] | null;
  sachgebiet: string[] | null;
  datum: string | null;
}

export interface Promise_ {
  id: number;
  quote: string;
  normalized: string;
  policy_area: string | null;
  page_number: number | null;
  commitment: string | null;
  testability: string | null;
  direction: string | null;
  short_name: string;
  color: string | null;
  programm_art: string;
  programm: string | null;
  status: string | null;
  consistency: string | null;
  confidence: number | null;
  review_status: string | null;
  belege: number;
  /** Gesetzt, wenn das Zitat im Originaldokument wiedergefunden wurde. */
  quote_verified_at: string | null;
  quote_found_page: number | null;
  extracted_by: string | null;
  reviewed_by?: string | null;
  reviewed_at?: string | null;
}

export interface Evidence {
  id: number;
  act_type: string;
  direction: "erfuellt" | "teilweise" | "widerspricht" | "neutral";
  relevance: number | null;
  rationale: string;
  found_by: string | null;
  confirmed_by: string | null;
  vorgang_id: number | null;
  vorgang_titel: string | null;
  vorgang_typ: string | null;
  beratungsstand: string | null;
  dip_id: string | null;
  poll_id: number | null;
  poll_label: string | null;
  poll_date: string | null;
  accepted: boolean | null;
}

export interface Assessment {
  id: number;
  status: string;
  consistency: string | null;
  confidence: number | null;
  reasoning: string;
  caveats: string | null;
  assessed_by: string;
  assessed_at: string;
  is_current: boolean;
  review_status: string;
}

export interface PromiseDetailData {
  promise: Promise_ & {
    partei: string;
    source_url: string | null;
    /** Pruefsumme der ausgewerteten PDF-Fassung. */
    sha256: string | null;
    retrieved_at: string | null;
    published_date: string | null;
    source_note: string | null;
  };
  belege: Evidence[];
  bewertungen: Assessment[];
}

export interface Program {
  id: number;
  kind: string;
  title: string | null;
  published_date: string | null;
  source_url: string | null;
  sha256: string | null;
  short_name: string;
  partei: string;
  color: string | null;
  versprechen: number;
}

export interface ScoreRow {
  short_name: string;
  color: string | null;
  rolle: "regierung" | "opposition" | null;
  versprechen_gesamt: number;
  bewertet: number;
  erfuellt: number;
  teilweise: number;
  in_arbeit: number;
  gebrochen: number;
  nicht_angegangen: number;
  konsistent: number;
  widerspruechlich: number;
}

/** Betreiberangaben fuer Impressum und Datenschutzerklaerung.
 *  Kommen aus der Serverumgebung, nicht aus dem Quelltext. */
export interface SiteInfo {
  betreiber: string;
  strasse: string;
  plz_ort: string;
  land: string;
  email: string;
  telefon: string;
  /** Nur bei juristischen Personen belegt (§ 5 DDG). */
  vertretung: string;
  registergericht: string;
  registernummer: string;
  ustid: string;
  verantwortlich: string;
  domain: string;
  /** Hauptangebot desselben Betreibers, leer wenn es keines gibt. */
  hauptseite: string;
  logs_tage: string;
  /** false, wenn Pflichtangaben fehlen -- dann zeigt die Seite einen Hinweis. */
  vollstaendig: boolean;
}

export interface Paged<T> {
  total: number;
  items: T[];
}

export interface Counted {
  anzahl: number;
}
